import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Formik,
  Form,
  Field,
  ErrorMessage as FormikErrorMessage,
} from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { updateNotificationAsync } from "../../redux/slices/notificationsSlice";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";

interface NotificationData {
  id: number;
  title: string;
  content: string;
  date: string;
  time: string;
  repeat: string;
}

const EditNotification: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notification } = location.state || {};

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!notification) {
    navigate("/notifications");
    return null;
  }

  // const formatDate = (dateString: string) => {
  //     if (!dateString) return '';
  //     const [year, month, day] = dateString.split('-');
  //     return `${day}/${month}/${year}`;
  // };

  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const parseDate = (formattedDate: string) => {
    if (!formattedDate) return "";
    const [day, month, year] = formattedDate.split("/");
    return `${year}-${month}-${day}`;
  };

  const validationSchema = Yup.object({
    title: Yup.string()
      .required("Title is required")
      .max(100, "Max 100 characters"),
    content: Yup.string()
      .required("Content is required")
      .max(1000, "Max 1000 characters"),
    date: Yup.string().required("Date is required"),
    time: Yup.string().required("Time is required"),
    repeat: Yup.string().required("Repeat selection is required"),
  });

  const handleSubmit = async (values: NotificationData) => {
    try {
      await dispatch(
        updateNotificationAsync({ id: values.id, data: values }) as any
      );
      setSuccessMessage("Notification updated successfully!");
      setTimeout(() => navigate("/notifications"), 2000);
    } catch {
      setErrorMessage("Failed to update notification.");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Edit Notification" />
      <div className="mb-4">
        <BackButton onClick={() => navigate(-1)} />
      </div>
      <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md">
        <Formik
          initialValues={{
            id: notification.id,
            title: notification.title,
            content: notification.content,
            date: parseDate(notification.date),
            time: notification.time,
            repeat: notification.repeat,
          }}
          validationSchema={validationSchema}
          validate={(values) => {
            const errors: Record<string, string> = {};
            const today = getTodayDate();
            const currentTime = getCurrentTime();

            if (values.date === today && values.time < currentTime) {
              errors.time = "Time cannot be earlier than the current time!";
            }

            return errors;
          }}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">
                  Notification Title
                </label>
                <Field
                  type="text"
                  name="title"
                  className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                />
                <FormikErrorMessage
                  name="title"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2">
                  Notification Content
                </label>
                <Field
                  as="textarea"
                  name="content"
                  className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                  maxLength={1000}
                />
                <FormikErrorMessage
                  name="content"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-2">Date</label>
                  <Field
                    type="date"
                    name="date"
                    min={getTodayDate()}
                    className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                  />
                  <FormikErrorMessage
                    name="date"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Time</label>
                  <Field
                    name="time"
                    type="time"
                    className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                  />
                  <FormikErrorMessage
                    name="time"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Repeat</label>
                <Field
                  as="select"
                  name="repeat"
                  className="w-full px-3 py-2 border rounded-md focus:ring focus:ring-blue-300"
                >
                  <option value="Daily">Daily</option>
                  <option value="Weekdays">Weekdays</option>
                  <option value="Weekends">Weekends</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </Field>
                <FormikErrorMessage
                  name="repeat"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <button
                type="submit"
                className="w-full py-2 bg-yellow-600 text-white font-semibold rounded-md hover:bg-yellow-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isSubmitting || !dirty}
              >
                {isSubmitting ? "Updating..." : "Update Notification"}
              </button>
            </Form>
          )}
        </Formik>
      </div>

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}
    </div>
  );
};

export default EditNotification;
